import { batchActions } from './utils';
import { setData } from './data';
import { setStatus, unsetStatus } from './status';
import {
  LOADING,
  ERROR,
  selectUserShowsStatus,
  selectUserShows,
  selectMe,
  selectDateRange,
  selectPendingPodcastsStatus,
  selectCurrentNetworkId,
} from './selectors';
import { toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import { Thunk, DateRangeWithLabel, PaginatedShows, PendingPodcast } from '../types';
import { getUserShows } from '../lib/Api';

const KEY = 'meShows';
const TABLE_PAGE_SIZE = 50;

export const loadUserShows = (
  filter: string,
  page: number,
  sortBy: string,
  sortDescending: boolean,
): Thunk => async (dispatch, getState) => {
  const { startDate: start, endDate: end } = selectDateRange(getState()) as DateRangeWithLabel;
  /* istanbul ignore next */
  const me = selectMe(getState());
  // The BE will infer the network that a user has permission for.
  // In the case of God Mode users we can pass a network URL parameter to only load podcasts for that network
  /* istanbul ignore next */
  const network = me!.permissions.godMode ? selectCurrentNetworkId(getState()) : undefined;

  /* istanbul ignore next */
  if (selectUserShowsStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [KEY]: LOADING }, 'me/LOAD_USER_SHOWS_REQUEST'));

  try {
    const data = await getUserShows({
      page,
      searchTerm: filter,
      start,
      end,
      sortBy,
      sortOrder: sortDescending
        ? /* istanbul ignore next */
          'descending'
        : 'ascending',
      limit: TABLE_PAGE_SIZE,
      networkId: network,
    });

    dispatch(
      batchActions(
        [
          setData({
            [KEY]: {
              ...data,
              shows: data.shows.map(show => {
                return {
                  ...show,
                  starts: show.starts === undefined ? /* istanbul ignore next */ null : show.starts,
                  streams:
                    show.streams === undefined ? /* istanbul ignore next */ null : show.streams,
                  listeners:
                    show.listeners === undefined ? /* istanbul ignore next */ null : show.listeners,
                };
              }),
            },
          }),
          unsetStatus([KEY]),
        ],
        'me/LOAD_USERS_SHOWS_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        const data = selectUserShows(getState())!;
        dispatch(
          batchActions(
            [
              setStatus({ [KEY]: ERROR }),
              setData({ [KEY]: { ...data, currentPage: page, shows: [] } }),
            ],
            'audience/LOAD_USER_SHOWS_ERROR',
          ),
        );
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};

export const loadPendingPodcasts = (): Thunk => async (dispatch, getState, { request }) => {
  const key = 'pendingPodcasts';
  const endpoint = `${PODCASTERS_SERVICE}/v0/user/submissions?filter=unpublished`;

  /* istanbul ignore next */
  if (selectPendingPodcastsStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'me/LOAD_PENDING_PODCASTS_REQUEST'));

  try {
    const data: {
      shows: PendingPodcast[];
    } & PaginatedShows = await request.get(endpoint).then(toJSON);

    dispatch(
      batchActions(
        [setData({ [key]: data.shows }), unsetStatus([key])],
        'me/LOAD_PENDING_PODCASTS_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_PENDING_PODCASTS_ERROR'));
        break;
      }
      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};
